// Mobile Menu Functionality
class SlideMenu {
    constructor() {
        this.menuButton = document.querySelector('[data-menu-toggle]');
        this.menuPanel = document.querySelector('[data-menu-panel]');
        this.menuOverlay = document.querySelector('[data-menu-overlay]');
        this.menuLinks = document.querySelectorAll('.menu-link');
        
        this.isOpen = false;
        this.focusableElements = [];
        this.firstFocusableElement = null;
        this.lastFocusableElement = null;
        this.previouslyFocusedElement = null;
        
        this.init();
    }
    
    init() {
        if (!this.menuButton || !this.menuPanel) return;
        
        // Set up focusable elements
        this.updateFocusableElements();
        
        // Event listeners
        this.menuButton?.addEventListener('click', () => this.toggle());
        this.menuOverlay?.addEventListener('click', () => this.close());
        
        // Keyboard navigation
        document.addEventListener('keydown', (e) => this.handleKeyDown(e));
        this.menuPanel.addEventListener('keydown', (e) => this.handleMenuKeyDown(e));
        
        // Handle escape key globally
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isOpen) {
                this.close();
            }
        });
    }
    
    updateFocusableElements() {
        const focusableSelectors = [
            '.menu-link',
            'button:not([disabled])',
            'input:not([disabled])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            '[tabindex]:not([tabindex="-1"])'
        ].join(', ');
        
        this.focusableElements = Array.from(
            this.menuPanel.querySelectorAll(focusableSelectors)
        ).filter(el => !el.hasAttribute('disabled') && el.offsetParent !== null);
        
        this.firstFocusableElement = this.focusableElements[0];
        this.lastFocusableElement = this.focusableElements[this.focusableElements.length - 1];
    }
    
    toggle() {
        if (this.isOpen) {
            this.close();
        } else {
            this.open();
        }
    }
    
    open() {
        this.isOpen = true;
        this.previouslyFocusedElement = document.activeElement;
        
        // Update DOM
        this.menuButton.setAttribute('aria-expanded', 'true');
        this.menuButton.classList.add('active');
        this.menuPanel.classList.add('active');
        this.menuOverlay.classList.add('active');
        this.menuOverlay.setAttribute('aria-hidden', 'false');
        
        // Prevent body scroll
        // document.body.style.overflow = 'hidden';
        
        // Focus management
        this.updateFocusableElements();
        
        // Focus the first element after a brief delay to ensure the menu is visible
        setTimeout(() => {
            if (this.firstFocusableElement) {
                this.firstFocusableElement.focus();
            }
        }, 100);
        
        // Announce to screen readers
        this.announceToScreenReader('Navigation menu opened');
    }
    
    close() {
        this.isOpen = false;
        
        // Update DOM
        this.menuButton.setAttribute('aria-expanded', 'false');
        this.menuButton.classList.remove('active');
        this.menuPanel.classList.remove('active');
        this.menuOverlay.classList.remove('active');
        this.menuOverlay.setAttribute('aria-hidden', 'true');
        
        // Restore body scroll
        document.body.style.overflow = '';
        
        // Restore focus
        if (this.previouslyFocusedElement) {
            this.previouslyFocusedElement.focus();
        }
        
        // Announce to screen readers
        this.announceToScreenReader('Navigation menu closed');
    }
    
    handleKeyDown(e) {
        // Handle menu button activation
        if (e.target === this.menuButton && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            this.toggle();
        }
    }
    
    handleMenuKeyDown(e) {
        if (!this.isOpen) return;
        
        // Tab key - focus trapping
        if (e.key === 'Tab') {
            if (e.shiftKey) {
                // Shift + Tab - move to previous element
                if (document.activeElement === this.firstFocusableElement) {
                    e.preventDefault();
                    this.lastFocusableElement?.focus();
                }
            } else {
                // Tab - move to next element
                if (document.activeElement === this.lastFocusableElement) {
                    e.preventDefault();
                    this.firstFocusableElement?.focus();
                }
            }
        }
        
        // Arrow key navigation for menu links
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            const currentIndex = this.focusableElements.indexOf(document.activeElement);
            if (currentIndex !== -1) {
                e.preventDefault();
                let nextIndex;
                
                if (e.key === 'ArrowDown') {
                    nextIndex = currentIndex + 1;
                    if (nextIndex >= this.focusableElements.length) {
                        nextIndex = 0;
                    }
                } else {
                    nextIndex = currentIndex - 1;
                    if (nextIndex < 0) {
                        nextIndex = this.focusableElements.length - 1;
                    }
                }
                
                this.focusableElements[nextIndex]?.focus();
            }
        }
        
        // Home/End keys
        if (e.key === 'Home') {
            e.preventDefault();
            this.firstFocusableElement?.focus();
        } else if (e.key === 'End') {
            e.preventDefault();
            this.lastFocusableElement?.focus();
        }
    }
    
    announceToScreenReader(message) {
        const announcement = document.createElement('div');
        announcement.setAttribute('aria-live', 'polite');
        announcement.setAttribute('aria-atomic', 'true');
        announcement.className = 'sr-only';
        announcement.textContent = message;
        
        document.body.appendChild(announcement);
        
        setTimeout(() => {
            document.body.removeChild(announcement);
        }, 1000);
    }
}

// Initialize slide menu when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        new SlideMenu();
    });
} else {
    new SlideMenu();
}

// Export for use in templates
window.SlideMenu = SlideMenu;
